/**
 * Content Strategy Service (Frontend)
 *
 * Frontend service for the HEFT Content Strategy Generator.
 * Handles generating, fetching, updating, and routing content strategy packs.
 */

import { createApiRequest } from '../api/createApiRequest'

export type IdeaStatus = 'pending' | 'approved' | 'rejected'
export type MethodFit = 'high' | 'medium' | 'low'
export type ContentStrategyPackStatus =
    | 'generating'
    | 'draft'
    | 'approved'
    | 'partially_approved'
    | 'generating_failed'

export interface ContentIdeaData {
    _id: string
    idea_id: string
    topic: string
    headline_angle: string
    formats: string[]
    rationale: string
    estimated_strength: MethodFit
    status: IdeaStatus
    routed_to_keyword_id?: string
    routed_to_campaign_id?: string
}

export interface MethodStrategyData {
    _id: string
    method: string
    method_fit: MethodFit
    source_story_ids: Array<{ _id: string; title: string; story_strength?: string }>
    content_ideas: ContentIdeaData[]
}

export interface ContentStrategyPackData {
    _id: string
    session_id: string
    subject_id?: string
    subject_type?: string
    subject_name?: string
    pack_id?: string
    keyword_id?: string
    status: ContentStrategyPackStatus
    method_strategies: MethodStrategyData[]
    processing_attempts: number
    last_error?: string
    org: string
    createdAt: string
    updatedAt: string
}

export interface RouteIdeaPayload {
    target: 'keyword' | 'campaign'
    keywordId?: string
    keywordText?: string
}

// ---------------------------------------------------------------------------

const generateContentStrategy = (sessionId: string) =>
    createApiRequest<{ packId: string }>({
        url: `/campaigns/interview/sessions/${sessionId}/generate-content-strategy`,
        method: 'POST',
    })
void generateContentStrategy

const getContentStrategyPack = (packId: string) =>
    createApiRequest<{ pack: ContentStrategyPackData }>({
        url: `/campaigns/content-strategy-packs/${packId}`,
        method: 'GET',
    })
void getContentStrategyPack

const updateIdeaStatus = (
    packId: string,
    methodStrategyId: string,
    ideaId: string,
    status: IdeaStatus
) =>
    createApiRequest<{ ok: boolean; packStatus: ContentStrategyPackStatus }>({
        url: `/campaigns/content-strategy-packs/${packId}/methods/${methodStrategyId}/ideas/${ideaId}`,
        method: 'PATCH',
        data: { status },
    })
void updateIdeaStatus

const routeIdea = (
    packId: string,
    methodStrategyId: string,
    ideaId: string,
    payload: RouteIdeaPayload
) =>
    createApiRequest<{ ok: boolean; keywordId?: string; campaignId?: string }>({
        url: `/campaigns/content-strategy-packs/${packId}/methods/${methodStrategyId}/ideas/${ideaId}/route`,
        method: 'POST',
        data: payload as unknown as Record<string, unknown>,
    })
void routeIdea

export const contentStrategyService = {
    generateContentStrategy,
    getContentStrategyPack,
    updateIdeaStatus,
    routeIdea,
}
